import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CategoryDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  categoryName: string;
  hasChildren?: boolean;
}

export const CategoryDeleteModal: React.FC<CategoryDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  categoryName,
  hasChildren = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error deleting category:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-md w-full"
        style={{ boxShadow: '4px 4px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4 flex items-center justify-between">
          <h2
            className="text-white uppercase"
            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
          >
            {t('delete_modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-[#077f5d]"
            style={{ border: '2px solid #077f5d' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            {t('delete_modal.confirm_message')}{' '}
            <span className="font-semibold">"{categoryName}"</span> {t('delete_modal.confirm_suffix')}
          </p>

          {hasChildren && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                {t('delete_modal.children_warning')}
              </p>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 p-6 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '10px',
              boxShadow: '4px 4px 0 #077f5d'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
            disabled={loading}
          >
            {t('delete_modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-3 bg-red-600 text-white border-2 border-red-800 font-bold uppercase disabled:opacity-50"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '10px',
              boxShadow: '6px 6px 0 #991b1b'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.boxShadow = '4px 4px 0 #991b1b';
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '6px 6px 0 #991b1b';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            {loading ? t('delete_modal.deleting') : t('delete_modal.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};
